import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Calendar, ClipboardList, Watch } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { getPlayers, getWellness, getRPE, getOuraSummary, getOuraConnections } from '../api';
import MissingSubmissionsModal from '../components/MissingSubmissionsModal';

const today = () => new Date().toISOString().split('T')[0];

function getScoreColor(val, inverse = false) {
  if (!val) return 'var(--text-muted)';
  const pct = val / 10;
  if (inverse) return pct >= 0.7 ? '#ef4444' : pct >= 0.4 ? '#f59e0b' : '#10b981';
  return pct >= 0.7 ? '#10b981' : pct >= 0.4 ? '#f59e0b' : '#ef4444';
}

function getEfficiencyColor(val) {
  if (val == null) return 'var(--text-muted)';
  if (val >= 71) return '#10b981';
  if (val >= 41) return '#f59e0b';
  return '#ef4444';
}

function ScoreBadge({ val, inverse = false, mode = 'score', unit = '/10' }) {
  const color = mode === 'efficiency' ? getEfficiencyColor(val) : getScoreColor(val, inverse);
  return (
    <span style={{ fontWeight: 700, color, fontSize: 14 }}>
      {val != null ? `${val}${unit}` : <span style={{ color: 'var(--text-dimmed)' }}>—</span>}
    </span>
  );
}

function StatusDot({ submitted, flagged, hasPain }) {
  if (!submitted) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#ef4444', fontWeight: 600 }}><XCircle size={14} /> Missing</span>;
  if (hasPain)    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#f59e0b', fontWeight: 600 }}><AlertTriangle size={14} /> Pain flag</span>;
  if (flagged)    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#f59e0b', fontWeight: 600 }}><AlertTriangle size={14} /> Flagged</span>;
  return             <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#10b981', fontWeight: 600 }}><CheckCircle size={14} /> Submitted</span>;
}

// ── Date helpers ──────────────────────────────────────────────────────────────
function addDays(dateStr, n) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}
function dateRange(from, to) {
  const dates = [];
  let d = new Date(from);
  const end = new Date(to);
  while (d <= end) { dates.push(d.toISOString().split('T')[0]); d.setDate(d.getDate() + 1); }
  return dates;
}
function fmtShort(dateStr) {
  if (!dateStr) return '';
  const [, m, d] = dateStr.split('-');
  return `${d}/${m}`;
}

// ── Heatmap cell style ────────────────────────────────────────────────────────
function hmStyle(val, inverse, metricKey) {
  if (val == null) return { background: 'var(--bg-app)', color: 'var(--text-dimmed)' };
  // Sleep efficiency (0–100%)
  if (metricKey === 'sleepQuality') {
    if (val >= 80) return { background: 'rgba(16,185,129,0.22)', color: '#6ee7b7' };
    if (val >= 60) return { background: 'rgba(245,158,11,0.22)', color: '#fbbf24' };
    return { background: 'rgba(239,68,68,0.22)', color: '#f87171' };
  }
  // Sleep HRV (ms)
  if (metricKey === 'sleepHrv') {
    if (val >= 40) return { background: 'rgba(16,185,129,0.22)', color: '#6ee7b7' };
    if (val >= 25) return { background: 'rgba(245,158,11,0.22)', color: '#fbbf24' };
    return { background: 'rgba(239,68,68,0.22)', color: '#f87171' };
  }
  if (inverse) {
    if (val >= 7) return { background: 'rgba(239,68,68,0.22)', color: '#f87171' };
    if (val >= 4) return { background: 'rgba(245,158,11,0.22)', color: '#fbbf24' };
    return { background: 'rgba(16,185,129,0.22)', color: '#6ee7b7' };
  }
  if (val >= 7) return { background: 'rgba(16,185,129,0.22)', color: '#6ee7b7' };
  if (val >= 4) return { background: 'rgba(245,158,11,0.22)', color: '#fbbf24' };
  return { background: 'rgba(239,68,68,0.22)', color: '#f87171' };
}

// ── Trend arrow ───────────────────────────────────────────────────────────────
function TrendArrow({ current, avg, inverse = false }) {
  if (current == null || avg == null) return null;
  const diff = current - avg;
  if (Math.abs(diff) < 0.2) return <span style={{ color: 'var(--text-muted)', fontSize: 13 }}> →</span>;
  const up = diff > 0;
  const good = inverse ? !up : up;
  return (
    <span style={{ color: good ? '#10b981' : '#ef4444', fontSize: 13, fontWeight: 700 }}>
      {' '}{up ? '↑' : '↓'}
    </span>
  );
}

// ── Custom tooltip for trend chart ────────────────────────────────────────────
function WellnessTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 5 }}>{fmtShort(label)}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{p.name}</span>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)', marginLeft: 8 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Individual view ───────────────────────────────────────────────────────────
const METRICS = [
  { key: 'sleepQuality',   label: 'Sleep Efficiency', color: '#8b5cf6', inverse: false },
  { key: 'sleepHrv',       label: 'Sleep HRV',        color: '#10b981', inverse: false },
  { key: 'stressLevel',    label: 'Stress Level',     color: '#ef4444', inverse: true  },
  { key: 'musclesoreness', label: 'Muscle Soreness',  color: '#f59e0b', inverse: true  },
];

function IndividualView({ players, wellness, rpeData, playerOverride = null }) {
  const [playerId,     setPlayerId]    = useState('');
  const [preset,       setPreset]      = useState('14');
  const [customFrom,   setCustomFrom]  = useState(addDays(today(), -14));
  const [customTo,     setCustomTo]    = useState(today());
  const [hiddenLines,  setHiddenLines] = useState(new Set());

  const activePlayers = useMemo(
    () => [...players].filter(p => p.active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [players]
  );

  useEffect(() => {
    if (!playerId && activePlayers.length) setPlayerId(String(activePlayers[0].id));
  }, [activePlayers, playerId]);

  const overrideId = playerOverride?.id;
  useEffect(() => {
    if (overrideId) setPlayerId(String(overrideId));
  }, [overrideId]);

  const rangeFrom = preset === 'custom' ? customFrom : addDays(today(), -parseInt(preset));
  const rangeTo   = preset === 'custom' ? customTo   : today();

  const player = activePlayers.find(p => String(p.id) === playerId);

  // All wellness entries for this player in range
  const entries = useMemo(() => {
    if (!playerId) return [];
    return wellness
      .filter(e => String(e.playerId) === playerId && e.date >= rangeFrom && e.date <= rangeTo)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [wellness, playerId, rangeFrom, rangeTo]);

  // RPE for this player in range
  const playerRpe = useMemo(() => {
    if (!playerId) return [];
    return rpeData
      .filter(r => String(r.player_id) === playerId && (r.date || '').slice(0, 10) >= rangeFrom && (r.date || '').slice(0, 10) <= rangeTo)
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [rpeData, playerId, rangeFrom, rangeTo]);

  // Most recent entry
  const latest = entries.length ? entries[entries.length - 1] : null;

  // 7-day average before latest (for trend)
  const prev7 = useMemo(() => {
    if (!latest) return {};
    const cutoff = addDays(latest.date, -7);
    const prev = entries.filter(e => e.date < latest.date && e.date >= cutoff);
    const avg = {};
    METRICS.forEach(m => {
      const vals = prev.map(e => e[m.key]).filter(v => v != null);
      avg[m.key] = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    });
    return avg;
  }, [entries, latest]);

  // Date series + entry lookup
  const dates = dateRange(rangeFrom, rangeTo);
  const byDate = Object.fromEntries(entries.map(e => [e.date, e]));

  // Chart data
  const chartData = dates.map(date => {
    const e = byDate[date];
    const point = { date, label: fmtShort(date) };
    METRICS.forEach(m => { point[m.key] = e ? (e[m.key] ?? null) : null; });
    return point;
  });

  // RPE chart data
  const rpeByDate = {};
  playerRpe.forEach(r => {
    const d = (r.date || '').slice(0, 10);
    rpeByDate[d] = r;
  });
  const rpeChartData = [...new Set(playerRpe.map(r => (r.date || '').slice(0, 10)))]
    .sort()
    .map(d => ({ date: d, label: fmtShort(d), muscular: Number(rpeByDate[d]?.rpe_muscular) || null, cardiovascular: Number(rpeByDate[d]?.rpe_cardiovascular) || null }));

  // Flags — any red-coded parameter
  const flags = entries.filter(e =>
    e.otherPain ||
    (e.musclesoreness >= 7) ||
    (e.stressLevel >= 7) ||
    (e.sleepHrv > 0 && e.sleepHrv < 25) ||
    (e.sleepQuality != null && e.sleepQuality < 61)
  );

  const toggleLine = key => setHiddenLines(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const SEL = { padding: '6px 26px 6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.83rem', fontFamily: 'inherit', cursor: 'pointer', outline: 'none', appearance: 'none', WebkitAppearance: 'none' };

  if (!activePlayers.length) return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>No active players found.</div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Player */}
        {!playerOverride && (
          <div style={{ position: 'relative' }}>
            <select value={playerId} onChange={e => setPlayerId(e.target.value)} style={{ ...SEL, minWidth: 180 }}>
              {activePlayers.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
            </select>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}><polyline points="6 9 12 15 18 9" /></svg>
          </div>
        )}
        {/* Presets */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[['7','Last 7 days'],['14','Last 14 days'],['28','Last 28 days'],['custom','Custom']].map(([v, label]) => (
            <button key={v} onClick={() => setPreset(v)}
              style={{ padding: '5px 10px', borderRadius: 6, fontSize: '0.78rem', fontFamily: 'inherit', fontWeight: preset === v ? 600 : 400, border: preset === v ? '1px solid #3b82f6' : '1px solid var(--border)', background: preset === v ? '#3b82f6' : 'var(--bg-card)', color: preset === v ? '#fff' : 'var(--text-secondary)', cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.83rem', outline: 'none' }} />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>–</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.83rem', outline: 'none' }} />
          </>
        )}
      </div>

      {/* ── No data ── */}
      {entries.length === 0 && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 48, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>No wellness data found for {player?.name} in the selected period.</p>
        </div>
      )}

      {entries.length > 0 && (
        <>
          {/* ── Section 1: Current status cards ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            {METRICS.map(m => {
              const val = latest?.[m.key];
              const avg = prev7[m.key];
              const color = getScoreColor(val, m.inverse);
              return (
                <div key={m.key} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>{m.label}</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: val ? color : 'var(--text-dimmed)', lineHeight: 1 }}>
                    {val ?? '—'}
                    {val && <TrendArrow current={val} avg={avg} inverse={m.inverse} />}
                  </div>
                  {avg != null && <div style={{ fontSize: '0.7rem', color: 'var(--text-dimmed)', marginTop: 5 }}>7-day avg: {avg.toFixed(1)}</div>}
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-dimmed)', marginTop: 2 }}>as of {fmtShort(latest?.date)}</div>
                </div>
              );
            })}
          </div>

          {/* ── Section 2: Trend chart ── */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px 12px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>Wellness Trend</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {METRICS.map(m => (
                  <button key={m.key} onClick={() => toggleLine(m.key)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 99, fontSize: '0.72rem', fontFamily: 'inherit', fontWeight: 500, border: `1px solid ${hiddenLines.has(m.key) ? 'var(--border)' : m.color}`, background: hiddenLines.has(m.key) ? 'transparent' : `${m.color}20`, color: hiddenLines.has(m.key) ? 'var(--text-dimmed)' : m.color, cursor: 'pointer' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: hiddenLines.has(m.key) ? 'var(--border)' : m.color }} />
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: 'var(--text-muted)', fontSize: 9, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(dates.length / 10) - 1)} />
                  <YAxis domain={[0, 10]} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <ReferenceLine y={5} stroke="var(--border-strong)" strokeDasharray="4 3" />
                  <Tooltip content={<WellnessTip />} />
                  {METRICS.map(m => !hiddenLines.has(m.key) && (
                    <Line key={m.key} dataKey={m.key} name={m.label} stroke={m.color} strokeWidth={2}
                      dot={{ r: 3, fill: m.color }} activeDot={{ r: 5 }}
                      connectNulls={false} isAnimationActive={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Section 3: Heatmap ── */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>Heatmap</span>
            </div>
            <div style={{ overflowX: 'auto', padding: '12px 18px' }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 3, fontSize: '0.72rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', fontWeight: 600, fontSize: '0.66rem', color: 'var(--text-muted)', paddingRight: 12, whiteSpace: 'nowrap' }}>Metric</th>
                    {dates.map(d => (
                      <th key={d} style={{ textAlign: 'center', fontWeight: 400, fontSize: '0.62rem', color: 'var(--text-dimmed)', width: 32, minWidth: 32 }}>{fmtShort(d)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map(m => (
                    <tr key={m.key}>
                      <td style={{ paddingRight: 12, fontWeight: 600, color: m.color, fontSize: '0.72rem', whiteSpace: 'nowrap', paddingBottom: 3 }}>{m.label}</td>
                      {dates.map(d => {
                        const val = byDate[d]?.[m.key] ?? null;
                        const st = hmStyle(val, m.inverse, m.key);
                        return (
                          <td key={d} style={{ width: 32, height: 26, textAlign: 'center', borderRadius: 4, fontWeight: 600, ...st }}>
                            {val ?? '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Section 4: Flags ── */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px', marginBottom: 20 }}>
            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Flags & Notes</div>
            {flags.length === 0 ? (
              <div style={{ fontSize: '0.83rem', color: '#10b981' }}>No flags in this period ✓</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {flags.flatMap(e => {
                  const items = [];
                  if (e.otherPain)         items.push({ text: `Pain reported — ${e.painLocation?.join(', ') || 'unspecified'} (${e.painLevel}/10)`, color: '#ef4444' });
                  if (e.musclesoreness >= 7) items.push({ text: `High soreness — ${e.musclesoreness}/10`, color: '#ef4444' });
                  if (e.stressLevel >= 7)  items.push({ text: `High stress — ${e.stressLevel}/10`, color: '#f59e0b' });
                  if (e.sleepHrv > 0 && e.sleepHrv < 25) items.push({ text: `Low HRV — ${e.sleepHrv} ms`, color: '#f59e0b' });
                  if (e.sleepQuality != null && e.sleepQuality < 61) items.push({ text: `Low sleep efficiency — ${e.sleepQuality}%`, color: '#ef4444' });
                  return items.map((item, i) => (
                    <div key={`${e.date}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.82rem' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', minWidth: 40 }}>{fmtShort(e.date)}</span>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                      <span style={{ color: item.color, fontWeight: 500 }}>{item.text}</span>
                    </div>
                  ));
                })}
              </div>
            )}
          </div>

          {/* ── Section 5: RPE trend ── */}
          {rpeChartData.length > 0 && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>Session RPE</span>
                <div style={{ display: 'flex', gap: 14, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#f59e0b', display: 'inline-block' }} />Muscular</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#3b82f6', display: 'inline-block' }} />Cardiovascular</span>
                </div>
              </div>
              <div style={{ height: 160 }}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={rpeChartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: 'var(--text-muted)', fontSize: 9, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                      formatter={(v, n) => [v, n === 'muscular' ? 'Muscular' : 'Cardiovascular']}
                    />
                    <Bar dataKey="muscular"       fill="#f59e0b" radius={[3,3,0,0]} maxBarSize={28} />
                    <Bar dataKey="cardiovascular" fill="#3b82f6" radius={[3,3,0,0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function WellnessStaff({ playerOverride = null, forceIndividual = false }) {
  const [players, setPlayers]       = useState([]);
  const [wellness, setWellness]     = useState([]);
  const [rpeData, setRpeData]       = useState([]);
  const [ouraSummary, setOuraSummary]         = useState([]);
  const [ouraConnections, setOuraConnections] = useState([]);
  const [selectedDate, setSelectedDate] = useState(today());
  const [expandedId, setExpandedId] = useState(null);
  const [view, setView]             = useState(forceIndividual ? 'individual' : 'team');
  const [showMissingModal, setShowMissingModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, w, rpe, oSummary, oConn] = await Promise.all([
          getPlayers(), getWellness(), getRPE(), getOuraSummary(), getOuraConnections(),
        ]);
        setPlayers(p);
        setRpeData(rpe || []);
        setOuraSummary(oSummary || []);
        setOuraConnections(oConn || []);
        setWellness(w.map(e => ({
          ...e,
          playerId: String(e.player_id),
          date: e.date ? e.date.slice(0, 10) : '',
          sleepHours: e.sleep_hours,
          sleepQuality: e.sleep_quality,
          sleepHrv: e.sleep_hrv,
          musclesoreness: e.muscle_soreness,
          sorenessLocation: typeof e.soreness_location === 'string' ? JSON.parse(e.soreness_location || '[]') : (e.soreness_location || []),
          stressLevel: e.stress_level,
          otherPain: e.other_pain,
          painLevel: e.pain_level,
          painLocation: typeof e.pain_location === 'string' ? JSON.parse(e.pain_location || '[]') : (e.pain_location || []),
          submittedAt: e.submitted_at,
        })));
      } catch (err) {
        console.error('Failed to load:', err);
      }
    };
    load();
  }, []);

  const activePlayers = players.filter(p => p.active);
  const getEntry = playerId => wellness.find(e => String(e.playerId) === String(playerId) && e.date === selectedDate);

  // Readiness/sleep score/resting HR never had a home in the wellness table -- kept as a
  // separate lookup rather than merged into `wellness` entries, so a connected player's
  // Oura data can't be mistaken for a submitted questionnaire (soreness/stress/pain stay
  // manual, and "submitted" still means that form was filled in).
  const ouraByKey = useMemo(() => {
    const map = new Map();
    ouraSummary.forEach(s => map.set(`${s.player_id}|${(s.day || '').slice(0, 10)}`, s));
    return map;
  }, [ouraSummary]);
  const getOuraEntry = playerId => ouraByKey.get(`${playerId}|${selectedDate}`);

  const ouraConnByPlayer = useMemo(() => {
    const map = new Map();
    ouraConnections.forEach(c => map.set(String(c.player_id), c));
    return map;
  }, [ouraConnections]);
  const isOuraConnected = playerId => {
    const c = ouraConnByPlayer.get(String(playerId));
    return !!c && !c.revoked_at;
  };

  const submitted = activePlayers.filter(p => getEntry(p.id));
  const missing   = activePlayers.filter(p => !getEntry(p.id));
  const isFlagged = e => !!(
    e?.otherPain ||
    (e?.musclesoreness >= 7) ||
    (e?.stressLevel >= 7) ||
    (e?.sleepHrv > 0 && e?.sleepHrv < 25) ||
    (e?.sleepQuality != null && e?.sleepQuality < 61)
  );
  const flagged = submitted.filter(p => { const e = getEntry(p.id); return e && isFlagged(e); });

  // ── Table sorting ──────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = useState('squadNumber');
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  // Status rank drives ascending sort toward "needs attention first": missing, then pain,
  // then flagged, then a clean submission.
  const statusRank = p => {
    const e = getEntry(p.id);
    if (!e) return 0;
    if (e.otherPain) return 1;
    if (isFlagged(e)) return 2;
    return 3;
  };
  const TABLE_COLUMNS = [
    { key: 'squadNumber',    label: '#',          value: p => (p.squadNumber != null ? Number(p.squadNumber) : null) },
    { key: 'name',           label: 'Player',     value: p => p.name || '' },
    { key: 'status',         label: 'Status',     value: statusRank },
    { key: 'sleepHours',     label: 'Sleep hrs',  value: p => getEntry(p.id)?.sleepHours ?? null },
    { key: 'sleepQuality',   label: 'Efficiency', value: p => getEntry(p.id)?.sleepQuality ?? null },
    { key: 'sleepHrv',       label: 'Sleep HRV',  value: p => getEntry(p.id)?.sleepHrv ?? null },
    { key: 'readiness',      label: 'Readiness',  value: p => getOuraEntry(p.id)?.readiness_score ?? null },
    { key: 'sleepScore',     label: 'Sleep Score', value: p => getOuraEntry(p.id)?.sleep_score ?? null },
    { key: 'restingHr',      label: 'Resting HR', value: p => getOuraEntry(p.id)?.resting_hr ?? null },
    { key: 'musclesoreness', label: 'Soreness',   value: p => getEntry(p.id)?.musclesoreness ?? null },
    { key: 'stressLevel',    label: 'Stress',     value: p => getEntry(p.id)?.stressLevel ?? null },
    { key: 'pain',           label: 'Pain',       value: p => { const e = getEntry(p.id); if (!e) return null; return e.otherPain ? 10 + (e.painLevel || 0) : 0; } },
  ];
  const sortedActivePlayers = useMemo(() => {
    const col = TABLE_COLUMNS.find(c => c.key === sortKey) || TABLE_COLUMNS[0];
    const withVal = [], withNull = [];
    activePlayers.forEach(p => {
      const v = col.value(p);
      (v == null ? withNull : withVal).push({ p, v });
    });
    withVal.sort((a, b) => {
      const cmp = typeof a.v === 'string' ? a.v.localeCompare(b.v) : a.v - b.v;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return [...withVal, ...withNull].map(x => x.p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlayers, sortKey, sortDir, wellness, selectedDate]);

  // ── Muscle soreness by location — counts how many players named each region today, not a
  // per-region severity (the data model only has one whole-player musclesoreness score per
  // entry, applying to every region that player selected — see WellnessInput.js).
  const sorenessEntries = submitted
    .map(p => getEntry(p.id))
    .filter(e => e?.musclesoreness != null && (e.sorenessLocation || []).length > 0);
  const regionCounts = {};
  sorenessEntries.forEach(e => {
    e.sorenessLocation.forEach(loc => { regionCounts[loc] = (regionCounts[loc] || 0) + 1; });
  });
  const sorenessChartData = Object.entries(regionCounts)
    .map(([region, count]) => ({ region, count }))
    .sort((a, b) => b.count - a.count);
  const avgSoreness = sorenessEntries.length
    ? sorenessEntries.reduce((s, e) => s + e.musclesoreness, 0) / sorenessEntries.length
    : null;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Wellness</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
            {view === 'team' ? 'Daily team overview' : 'Individual player trend'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* View toggle */}
          {!forceIndividual && (
            <div style={{ display: 'flex', background: 'var(--bg-app)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
              {[['team','Team'],['individual','Individual']].map(([v, label]) => (
                <button key={v} onClick={() => setView(v)}
                  style={{ padding: '5px 14px', borderRadius: 6, fontSize: '0.82rem', fontFamily: 'inherit', fontWeight: v === view ? 600 : 400, border: 'none', background: v === view ? '#3b82f6' : 'transparent', color: v === view ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', transition: 'all 0.15s' }}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {/* Date picker (team view only) */}
          {view === 'team' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '8px 14px' }}>
              <Calendar size={16} color="var(--text-muted)" />
              <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: 14, outline: 'none', cursor: 'pointer' }} />
            </div>
          )}
          {/* Missing Submissions popup (explicit request, 2026-09-14) — who's missing Wellness/
              Pitch RPE across a chosen date range, screenshot-friendly for sharing to staff. */}
          <button onClick={() => setShowMissingModal(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
            <ClipboardList size={15} /> Missing Submissions
          </button>
        </div>
      </div>

      {showMissingModal && (
        <MissingSubmissionsModal
          players={players}
          wellness={wellness}
          rpeData={rpeData}
          onClose={() => setShowMissingModal(false)}
        />
      )}

      {/* ── Individual view ── */}
      {view === 'individual' && (
        <IndividualView players={players} wellness={wellness} rpeData={rpeData} playerOverride={playerOverride} />
      )}

      {/* ── Team view (unchanged) ── */}
      {view === 'team' && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            {[
              { label: 'Total Players', val: activePlayers.length, color: '#3b82f6' },
              { label: 'Submitted',     val: submitted.length,     color: '#10b981' },
              { label: 'Missing',       val: missing.length,       color: '#ef4444' },
              { label: 'Flagged',       val: flagged.length,       color: '#f59e0b' },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)', textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 800, color }}>{val}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Submission progress bar */}
          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)', marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Submission progress</span>
              <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{submitted.length} / {activePlayers.length}</span>
            </div>
            <div style={{ height: 10, background: 'var(--bg-app)', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${activePlayers.length ? (submitted.length / activePlayers.length) * 100 : 0}%`, background: '#10b981', borderRadius: 99, transition: 'width 0.4s' }} />
            </div>
          </div>

          {/* Flagged players */}
          {flagged.length > 0 && (
            <div style={{ background: '#451a03', border: '1px solid #92400e', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <AlertTriangle size={18} color="#f59e0b" />
                <span style={{ fontWeight: 700, color: '#fbbf24', fontSize: 15 }}>Players requiring attention ({flagged.length})</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {flagged.map(p => {
                  const e = getEntry(p.id);
                  const reasons = [];
                  if (e?.otherPain)                    reasons.push('Pain reported');
                  if (e?.musclesoreness >= 7)           reasons.push(`Soreness ${e.musclesoreness}/10`);
                  if (e?.stressLevel >= 7)              reasons.push(`Stress ${e.stressLevel}/10`);
                  if (e?.sleepHrv > 0 && e?.sleepHrv < 25) reasons.push(`Low HRV ${e.sleepHrv} ms`);
                  if (e?.sleepQuality != null && e?.sleepQuality < 61) reasons.push(`Low efficiency ${e.sleepQuality}%`);
                  return (
                    <div key={p.id} style={{ background: '#78350f', borderRadius: 8, padding: '8px 14px' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24' }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>{reasons.join(' · ')}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Muscle soreness by location */}
          <div style={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Muscle soreness by location</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              How many players named each region today — one report can cover multiple regions.
            </div>
            {sorenessChartData.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-dimmed)', padding: '12px 0' }}>No soreness reported for this date.</div>
            ) : (
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                <div style={{ flex: '2 1 360px', minWidth: 280 }}>
                  <ResponsiveContainer width="100%" height={Math.max(140, sorenessChartData.length * 28)}>
                    <BarChart data={sorenessChartData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="region" width={110} tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                        formatter={v => [`${v} player${v === 1 ? '' : 's'}`, 'Reported']}
                      />
                      <Bar dataKey="count" fill="#f59e0b" radius={[0, 3, 3, 0]} maxBarSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ flex: '1 1 160px', display: 'flex', flexDirection: 'row', gap: 12 }}>
                  <div style={{ flex: 1, background: 'var(--bg-app)', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#f59e0b' }}>{avgSoreness.toFixed(1)}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Avg soreness / 10</div>
                  </div>
                  <div style={{ flex: 1, background: 'var(--bg-app)', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#3b82f6' }}>{sorenessEntries.length}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Players reporting</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Missing players */}
          {missing.length > 0 && (
            <div style={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <XCircle size={18} color="#ef4444" />
                <span style={{ fontWeight: 700, color: '#f87171', fontSize: 15 }}>Not yet submitted ({missing.length})</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {missing.map(p => (
                  <div key={p.id} style={{ background: 'var(--bg-app)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '6px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
                    #{p.squadNumber} {p.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full player table */}
          <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>All Players</span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selectedDate}</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-app)' }}>
                    {TABLE_COLUMNS.map(col => (
                      <th key={col.key} onClick={() => toggleSort(col.key)}
                        style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: sortKey === col.key ? 'var(--text-primary)' : 'var(--text-muted)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                        {col.label}
                        <span style={{ marginLeft: 4, color: sortKey === col.key ? '#3b82f6' : 'var(--text-dimmed)', opacity: sortKey === col.key ? 1 : 0.5 }}>
                          {sortKey === col.key ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedActivePlayers.map((p, i) => {
                    const e = getEntry(p.id);
                    const isExpanded = expandedId === p.id;
                    return (
                      <React.Fragment key={p.id}>
                        <tr onClick={() => setExpandedId(isExpanded ? null : p.id)}
                          style={{ borderTop: '1px solid var(--border-strong)', cursor: 'pointer', background: isExpanded ? 'var(--bg-app)' : i % 2 === 0 ? 'transparent' : 'var(--bg-row-alt)' }}
                          onMouseEnter={el => el.currentTarget.style.background = 'var(--bg-row-hover)'}
                          onMouseLeave={el => el.currentTarget.style.background = isExpanded ? 'var(--bg-app)' : i % 2 === 0 ? 'transparent' : 'var(--bg-row-alt)'}>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>{p.squadNumber || '—'}</td>
                          <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {p.name}
                              <Watch size={13} color={isOuraConnected(p.id) ? '#10b981' : 'var(--text-dimmed)'} strokeWidth={2} title={isOuraConnected(p.id) ? 'Oura connected' : 'Oura not connected'} />
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px' }}><StatusDot submitted={!!e} flagged={e && isFlagged(e)} hasPain={!!e?.otherPain} /></td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: 14 }}>
                              {e?.sleepHours != null ? (() => { const h = Math.floor(e.sleepHours); const m = Math.round((e.sleepHours % 1) * 60); return m > 0 ? `${h}h ${m}m` : `${h}h`; })() : <span style={{ color: 'var(--text-dimmed)' }}>—</span>}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px' }}><ScoreBadge val={e?.sleepQuality} mode="efficiency" unit="%" /></td>
                          <td style={{ padding: '12px 16px' }}><ScoreBadge val={e?.sleepHrv} unit=" ms" /></td>
                          {/* Oura readiness/sleep scores are 0-100, so val/10 against ScoreBadge's
                              /10-tuned thresholds (>=7 green, >=4 amber) lands on 70/40 -- a
                              reasonable green/amber/red split for a 0-100 score, coincidentally. */}
                          <td style={{ padding: '12px 16px' }}><ScoreBadge val={getOuraEntry(p.id)?.readiness_score} unit="" /></td>
                          <td style={{ padding: '12px 16px' }}><ScoreBadge val={getOuraEntry(p.id)?.sleep_score} unit="" /></td>
                          {/* No color coding for resting HR -- ScoreBadge's thresholds assume
                              "higher is better" on a 0-100ish scale, neither of which holds for
                              bpm, and no HR-specific threshold was specified anywhere in the plan. */}
                          <td style={{ padding: '12px 16px', fontWeight: 700, fontSize: 14, color: 'var(--text-secondary)' }}>
                            {getOuraEntry(p.id)?.resting_hr != null ? `${getOuraEntry(p.id).resting_hr} bpm` : <span style={{ color: 'var(--text-dimmed)' }}>—</span>}
                          </td>
                          <td style={{ padding: '12px 16px' }}><ScoreBadge val={e?.musclesoreness} inverse /></td>
                          <td style={{ padding: '12px 16px' }}><ScoreBadge val={e?.stressLevel} inverse /></td>
                          <td style={{ padding: '12px 16px' }}>
                            {e ? (e.otherPain
                              ? <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 13 }}>⚠️ {e.painLevel}/10</span>
                              : <span style={{ color: '#10b981', fontSize: 13 }}>None</span>)
                              : <span style={{ color: 'var(--text-dimmed)' }}>—</span>}
                          </td>
                        </tr>
                        {isExpanded && e && (
                          <tr style={{ background: 'var(--bg-app)', borderTop: '1px solid var(--border-strong)' }}>
                            <td colSpan={12} style={{ padding: '16px 20px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                                {e.sorenessLocation?.length > 0 && (
                                  <div style={{ background: 'var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Soreness locations</div>
                                    <div style={{ fontSize: 13, color: '#f59e0b' }}>{e.sorenessLocation.join(', ')}</div>
                                  </div>
                                )}
                                {e.otherPain && e.painLocation?.length > 0 && (
                                  <div style={{ background: 'var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Pain locations</div>
                                    <div style={{ fontSize: 13, color: '#ef4444' }}>{e.painLocation.join(', ')}</div>
                                  </div>
                                )}
                                <div style={{ background: 'var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Submitted at</div>
                                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{e.submittedAt ? new Date(e.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
